import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationsStore } from '@/store/notificationsStore';
import { useCurrentProjectStore } from '@/store/currentProjectStore';
import type { RouteLocationRaw } from 'vue-router';
import { toRouteLocationRaw } from '@/menu/menuTargetRegistry';
import {
  buildViewRouteFromPresentation,
  getIconConfigForPresentation
} from '@/menu/targetPresentationRegistry';
import {
  getMenuObjectTypeIcon,
  getMenuObjectTypeLabel,
  type IconConfig
} from '@/menu/objectTypeRegistry';

// API imports - følger vue-dataflow regler
import { isPowerBiReportGuid } from '@/helpers/isPowerBiReportGuid';
import {
  fetchMenu,
  saveSystemObject,
  deleteSystemObject,
  undeleteSystemObject,
  saveSystemObjectOrder,
  applyMenuStructureImport,
  downloadMenuStructureExport,
  addSystemObjectTarget,
  getSystemObjectTargets,
  removeSystemObjectTarget,
  setDefaultSystemObjectTarget,
  previewMenuStructureImport,
  type MenuItem,
  type SystemObjectSavePayload,
  type SystemObjectOrderPayload,
  type SystemObjectTargetPayload,
  type MenuStructureApplyRequest,
  type MenuStructurePreviewResponse,
} from '@/api/endpoints/menu';
import { searchMenuTargets } from '@/api/endpoints/menuTargetSearch';
import type { MenuTargetSearchResult } from '@/api/endpoints/menuTargetSearch';
import { validateMenuIntegrity } from '@/api/endpoints/tableDefinitions';
import type { MenuIntegrityValidationResponse } from '@/api/endpoints/tableDefinitions';
import { resolveShortcutMenuItemName } from '@/menu/resolveShortcutMenuItemName';
import { wireText } from '@/helpers/wireText';

// Local interfaces
interface MenuType {
  name: string;
  objectType: string;
}

interface BreadcrumbItem {
  label: string;
}

interface ProcessingState {
  save: boolean;
  delete: boolean;
  undelete: boolean;
  reorder: boolean;
}

interface CreateShortcutWithPrimaryTargetParams {
  parentMenuItemKey: string | null;
  formName: string;
  target: MenuTargetSearchResult;
}

/**
 * Redigerbar draft for menystruktur-import-dialogen. Eies av storeMenu slik at
 * dialogen kan være en tynn, alltid-montert view (DOM-kravet for `<dialog>`):
 * åpne-actionen `startMenuImport` seeder ferskt; dialogen binder felt via
 * get/set-computeds; preview/apply-actionene eier feilhåndteringen.
 */
export interface MenuImportDraft {
  rootMenuItemKey: string;
  selectedFile: File | null;
  preview: MenuStructurePreviewResponse | null;
  selectedSheetName: string;
  suggestedSheetName: string;
  availableSheetNames: string[];
}

function emptyMenuImportDraft (): MenuImportDraft {
  return {
    rootMenuItemKey: '',
    selectedFile: null,
    preview: null,
    selectedSheetName: '',
    suggestedSheetName: '',
    availableSheetNames: []
  };
}

// Recursive helper function for mapping all children of the given item
// Defined outside store for better performance (doesn't use store scope)
function getAllSystemObjects (
  item: MenuItem,
  accumulator?: Map<string, MenuItem>
): Map<string, MenuItem> {
  const acc = accumulator ?? new Map<string, MenuItem>();
  const menuItemKey = String(item.menuItemKey || '').trim();
  if (menuItemKey) {
    acc.set(menuItemKey, item);
  }
  if (item.items) {
    for (const child of item.items) {
      getAllSystemObjects(child, acc);
    }
  }
  return acc;
}

/**
 * Utleder en menneskelesbar feildetalj fra et request-feilobjekt (ProblemDetails
 * detail/title, fallback til Error.message, til slutt en generisk hjelpetekst).
 * Eksportert så test-laget kan pinne mappingen direkte. Eier feil-detalj-utledning
 * for menypunkt-handlinger (flyttet fra MenuTab — FE er presentasjonslag).
 */
export function getRequestErrorDetail (error: unknown): string {
  const data = (error as { response?: { data?: { detail?: unknown; title?: unknown } } }).response?.data;

  const detail = wireText(data?.detail).trim();
  if (detail) {
    return detail;
  }

  const title = wireText(data?.title).trim();
  if (title) {
    return title;
  }

  const message = wireText((error as { message?: unknown })?.message).trim();
  if (message) {
    return message;
  }

  return 'Prøv igjen, eller kontakt teamet hvis feilen fortsetter.';
}

/** Normaliserer objectType (trim, lowercase). FE bruker kun objectType fra API, ikke typeId. */
function getEffectiveObjectType (item: { objectType?: string } | null | undefined): string {
  if (!item?.objectType?.trim()) return '';
  return item.objectType.trim().toLowerCase();
}

function getIcon (objectType: string | undefined): IconConfig {
  return getMenuObjectTypeIcon(objectType);
}

function normalizeMenuLookupKey (key: unknown): string | null {
  const maybeRef = key as { value?: unknown } | null;
  const raw = maybeRef && typeof maybeRef === 'object' && 'value' in maybeRef ? maybeRef.value : key;
  if (raw == null) return null;
  const asString = wireText(raw).trim();
  if (!asString) return null;
  return asString;
}

function isCanceledRequest (error: unknown): boolean {
  const err = error as { name?: string; code?: string };
  return err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
}

export const useMenuStore = defineStore('menuStore', () => {
  const notificationsStore = useNotificationsStore();
  const currentProjectStore = useCurrentProjectStore();
  const { projectId } = storeToRefs(currentProjectStore);

  // State
  const menuItems = ref<MenuItem[] | null>(null);
  const mapOfSystemObjects = ref<Map<string, MenuItem>>(new Map());
  const isMenuLoading = ref(false);
  const isMenuLoaded = ref(false);
  const activeParentIds = ref<Set<string>>(new Set());
  const breadcrumbs = ref<BreadcrumbItem[]>([]);
  const isProcessing = ref<ProcessingState>({
    save: false,
    delete: false,
    undelete: false,
    reorder: false,
  });
  const menuError = ref<string | null>(null);

  // Menystruktur-import-draft (eies her; dialogen er en tynn view)
  const importDraft = ref<MenuImportDraft>(emptyMenuImportDraft());
  const isPreviewingImport = ref(false);
  const isApplyingImport = ref(false);

  let menuFetchPromise: Promise<MenuItem[] | null | undefined> | null = null;
  let menuFetchId = 0;

  const menuTypes = ref<MenuType[]>([
    { name: getMenuObjectTypeLabel('shortcut') || 'Kobling', objectType: 'shortcut' },
    { name: getMenuObjectTypeLabel('system-object') || 'Datavisning', objectType: 'system-object' },
    { name: getMenuObjectTypeLabel('report') || 'Rapport', objectType: 'report' },
    { name: getMenuObjectTypeLabel('folder') || 'Mappe', objectType: 'folder' },
    { name: getMenuObjectTypeLabel('data-view') || 'Datavisning', objectType: 'data-view' },
  ]);

  const getMenuTypeName = computed(() => (objectType: string): string => {
    if (!objectType) {
      return '';
    }
    const menuType = menuTypes.value.find((item) => item.objectType === objectType);
    return menuType ? menuType.name : '';
  });

  // Functions
  // Per-objecttype rute-utledning, trukket ut fra getRoute for å holde kompleksiteten nede (S3776).
  // Hver helper beholder eksakt samme betingelser/parametre som grenen den erstattet.
  function getShortcutRoute (item: MenuItem, targetPublicId: string, menuItemKey: string): RouteLocationRaw | false {
    if (item.behaviorType) {
      const spec = buildViewRouteFromPresentation(item.behaviorType, targetPublicId, {
        projectSlug: currentProjectStore.projectSlug
      });
      if (spec) {
        return toRouteLocationRaw(spec, menuItemKey);
      }
    }
    return false;
  }

  function getReportRoute (item: MenuItem, menuItemKey: string): RouteLocationRaw | false {
    if (item.targetPublicId) {
      return toRouteLocationRaw({ name: 'report-public', params: { publicId: item.targetPublicId } }, menuItemKey);
    }
    return item.menuItemKey &&
      item.reportId &&
      isPowerBiReportGuid(item.reportId)
      ? toRouteLocationRaw({
        name: 'report',
        params: {
          menuItemKey: item.menuItemKey,
          reportId: item.reportId.trim().toLowerCase(),
        }
      }, menuItemKey)
      : false;
  }

  function getDataViewRoute (item: MenuItem, menuItemKey: string): RouteLocationRaw | false {
    if (item.targetPublicId) {
      return toRouteLocationRaw({
        name: 'data-view-public',
        params: { publicId: item.targetPublicId }
      }, menuItemKey);
    }
    return item.menuItemKey && item.dataViewId
      ? toRouteLocationRaw({
        name: 'data-view',
        params: { menuItemKey: item.menuItemKey, viewId: item.dataViewId }
      }, menuItemKey)
      : false;
  }

  function getRoute (item: MenuItem): RouteLocationRaw | false {
    const menuItemKey = String(item.menuItemKey || '').trim();

    const objectType = getEffectiveObjectType(item);
    const targetPublicId = String(item.targetPublicId || '').trim();

    if (objectType === 'shortcut') {
      return getShortcutRoute(item, targetPublicId, menuItemKey);
    }
    switch (objectType) {
      case 'folder':
        return false;
      case 'system-object':
        return false;
      case 'report':
        return getReportRoute(item, menuItemKey);
      case 'data-view':
        return getDataViewRoute(item, menuItemKey);
    }
    return false;
  }

  function isRouteValid (item: MenuItem): boolean {
    return getRoute(item) !== false;
  }

  function getMenuItemIcon (item: MenuItem | null | undefined): IconConfig {
    if (!item) {
      return getIcon(undefined);
    }
    const objectType = getEffectiveObjectType(item);
    if (objectType === 'shortcut' || objectType === 'data-view') {
      if (item.iconType) {
        return getIconConfigForPresentation(item.iconType);
      }
      return getIconConfigForPresentation(undefined);
    }
    return getIcon(item.objectType);
  }

  // Menu and Tree
  async function refreshMenusAndTree (): Promise<void> {
    if (!projectId.value) return;
    menuFetchPromise = null;
    const result = await getMenu();
    if (result === null) {
      throw new Error(menuError.value ?? 'Kunne ikke hente meny');
    }
  }

  async function getMenu (): Promise<MenuItem[] | undefined | null> {
    const activeProjectId = projectId.value;
    if (!activeProjectId) {
      menuItems.value = [];
      isMenuLoaded.value = true;
      return undefined;
    }

    if (menuFetchPromise !== null) {
      return menuFetchPromise;
    }

    const thisFetchId = ++menuFetchId;
    menuFetchPromise = (async () => {
      isMenuLoading.value = true;
      menuError.value = null;
      try {
        const data = await fetchMenu();
        menuItems.value = data ?? [];
        mapOfSystemObjects.value = createMapOfSystemObjects();
        isMenuLoaded.value = true;
        return data;
      } catch (error) {
        console.error('[storeMenu] Feil ved henting av meny:', error);
        menuItems.value = [];
        menuError.value = 'Kunne ikke hente meny';
        isMenuLoaded.value = true;
        return null;
      } finally {
        if (thisFetchId === menuFetchId) {
          isMenuLoading.value = false;
          menuFetchPromise = null;
        }
      }
    })();
    return menuFetchPromise;
  }

  function createMapOfSystemObjects (): Map<string, MenuItem> {
    const map = new Map<string, MenuItem>();
    if (!menuItems.value || !Array.isArray(menuItems.value)) {
      return map;
    }
    for (const item of menuItems.value) {
      getAllSystemObjects(item, map);
    }
    return map;
  }

  async function getSystemObject (menuItemKey: string): Promise<MenuItem | undefined> {
    if (!mapOfSystemObjects.value || mapOfSystemObjects.value.size === 0) {
      if (!projectId.value) {
        console.warn('[storeMenu] projectId ikke satt, kan ikke hente menu for getSystemObject');
        return undefined;
      }
      await getMenu();
    }
    return mapOfSystemObjects.value.get(menuItemKey) ?? mapOfSystemObjects.value.get(String(menuItemKey));
  }

  function getBreadcrumbForItem (
    menuItemKey: unknown
  ): BreadcrumbItem[] {
    const path: BreadcrumbItem[] = [];
    activeParentIds.value.clear();

    if (!mapOfSystemObjects.value) {
      console.warn('getBreadcrumbForItem: mapOfSystemObjects not initialized yet');
      return [];
    }

    const normalizedKey = normalizeMenuLookupKey(menuItemKey);
    if (normalizedKey == null) {
      console.error('breadcrumb - missing menu item key', menuItemKey);
      return [];
    }

    const item = mapOfSystemObjects.value.get(normalizedKey);
    if (item == null) {
      console.error('breadcrumb - failed looking up menu item for key', menuItemKey, mapOfSystemObjects);
      return [];
    }
    path.push({ label: item.name });

    let parentKey = item.parentMenuItemKey;
    while (parentKey != null) {
      const current = mapOfSystemObjects.value.get(parentKey);
      if (current == null) break;
      path.push({ label: current.name });
      activeParentIds.value.add(parentKey);
      parentKey = current.parentMenuItemKey ?? null;
    }

    return path.reverse();
  }

  function updateBreadcrumbs (
    menuItemKey: unknown
  ): void {
    if (normalizeMenuLookupKey(menuItemKey) == null) {
      breadcrumbs.value = [];
      return;
    }

    const path = getBreadcrumbForItem(menuItemKey);
    breadcrumbs.value = path.filter((item) => item.label !== undefined);
  }

  // Menu Edit - bruker API-funksjoner
  async function MenuItemSave (item: SystemObjectSavePayload): Promise<string | false> {
    isProcessing.value.save = true;
    try {
      const objectType = item.objectType?.trim() || 'system-object';
      const payload: SystemObjectSavePayload = {
        ...item,
        objectType
      };
      const returnedKey = await saveSystemObject(payload);
      await refreshMenusAndTree();
      notificationsStore.addSuccess('Menyelement ble opprettet/oppdatert.');
      const trimmed = String(returnedKey || '').trim();
      return trimmed || false;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke lagre menyelement');
      }
      return false;
    } finally {
      isProcessing.value.save = false;
    }
  }

  async function MenuItemDelete (menuItemKey: string): Promise<boolean> {
    isProcessing.value.delete = true;
    try {
      await deleteSystemObject(menuItemKey);
      await refreshMenusAndTree();

      notificationsStore.addSuccess('Menyelement ble slettet.');
      return true;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string; message?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke slette menyelement');
      }
      return false;
    } finally {
      isProcessing.value.delete = false;
    }
  }

  async function MenuItemUnDelete (menuItemKey: string): Promise<boolean> {
    isProcessing.value.undelete = true;
    try {
      await undeleteSystemObject(menuItemKey);
      await refreshMenusAndTree();

      notificationsStore.addSuccess('Menyelement ble gjenopprettet.');
      return true;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke gjenopprette menyelement');
      }
      return false;
    } finally {
      isProcessing.value.undelete = false;
    }
  }

  async function searchMenuSetupTargets (
    query: string,
    signal?: AbortSignal
  ): Promise<MenuTargetSearchResult[]> {
    const q = String(query || '').trim();
    if (!q) {
      return [];
    }
    try {
      const response = await searchMenuTargets(q, signal);
      return response.results ?? [];
    } catch (error: unknown) {
      if (isCanceledRequest(error)) {
        return [];
      }
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke søke etter koblinger');
      }
      return [];
    }
  }

  async function createShortcutWithPrimaryTarget (
    params: CreateShortcutWithPrimaryTargetParams
  ): Promise<boolean> {
    const resolvedName = resolveShortcutMenuItemName(params.formName, params.target.name);
    if (!resolvedName) {
      notificationsStore.addError('Må fylle ut navn eller velge et koblingsmål med navn');
      return false;
    }

    isProcessing.value.save = true;
    let createdKey: string | null = null;
    try {
      const savePayload: SystemObjectSavePayload = {
        name: resolvedName,
        objectType: 'shortcut',
        parentMenuItemKey: params.parentMenuItemKey,
      };
      const returnedKey = await saveSystemObject(savePayload);
      createdKey = String(returnedKey || '').trim();
      if (!createdKey) {
        notificationsStore.addError('Kunne ikke opprette menypunkt');
        return false;
      }

      await addSystemObjectTarget(createdKey, {
        targetType: String(params.target.targetType || '').trim().toLowerCase(),
        targetPublicId: String(params.target.targetPublicId || '').trim(),
        targetSubType: params.target.targetSubType ?? null,
      });
      await refreshMenusAndTree();
      notificationsStore.addSuccess('Menyelement ble opprettet/oppdatert.');
      return true;
    } catch (error: unknown) {
      if (createdKey) {
        try {
          await deleteSystemObject(createdKey);
        } catch {
          /* rollback best-effort */
        }
        try {
          await refreshMenusAndTree();
        } catch {
          /* ignore */
        }
      }
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke opprette kobling med mål');
      }
      return false;
    } finally {
      isProcessing.value.save = false;
    }
  }

  async function MenuSaveNewOrder (items: SystemObjectOrderPayload[]): Promise<boolean> {
    isProcessing.value.reorder = true;
    try {
      await saveSystemObjectOrder(items);
      await refreshMenusAndTree();
      notificationsStore.addSuccess('Rekkefølgen på menyelementene ble oppdatert.');
      return true;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke oppdatere rekkefølge');
      }
      return false;
    } finally {
      isProcessing.value.reorder = false;
    }
  }

  async function ExportMenuStructure (
    rootMenuItemKey: string,
    format: 'json' | 'xlsx',
    includeAnchor: boolean
  ): Promise<boolean> {
    try {
      await downloadMenuStructureExport(rootMenuItemKey, format, { includeAnchor });
      return true;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Eksport feilet');
      }
      return false;
    }
  }

  // --- Menystruktur-import-draft (åpne-action seeder; dialogen binder til draften) ---

  /**
   * Seeder import-draften ferskt for et rot-menypunkt. Kalles av parent-knappens
   * åpne-handler FØR dialogen vises (modelValue=true). Erstatter den tidligere
   * «seed-fresh-ved-åpning»-computeden i komponenten — ren state-init, ingen
   * side-effekt i en computed.
   */
  function startMenuImport (rootMenuItemKey: string): void {
    importDraft.value = { ...emptyMenuImportDraft(), rootMenuItemKey };
    isPreviewingImport.value = false;
    isApplyingImport.value = false;
  }

  /** Setter valgt fil og nullstiller avledet preview/ark-tilstand. */
  function setMenuImportFile (file: File): void {
    importDraft.value = {
      ...importDraft.value,
      selectedFile: file,
      preview: null,
      selectedSheetName: '',
      suggestedSheetName: '',
      availableSheetNames: []
    };
  }

  /** Setter valgt arkfane i draften (uten å re-previewe — det styrer dialogen). */
  function setMenuImportSheetName (sheetName: string): void {
    importDraft.value = { ...importDraft.value, selectedSheetName: sheetName };
  }

  /**
   * Forhåndsviser importen mot BE for den valgte fila/arket og oppdaterer draften.
   * Eier try/catch + feil-mapping (ProblemDetails → addProblem, ellers addError).
   * Returnerer true ved suksess. No-op (false) hvis ingen fil er valgt.
   */
  async function previewMenuImport (): Promise<boolean> {
    const file = importDraft.value.selectedFile;
    if (!file) return false;
    isPreviewingImport.value = true;
    importDraft.value = { ...importDraft.value, preview: null };
    try {
      const response = await previewMenuStructureImport(
        importDraft.value.rootMenuItemKey,
        file,
        importDraft.value.selectedSheetName || null
      );
      const previousSuggestedSheetName = importDraft.value.suggestedSheetName;
      const effectiveSuggestedSheetName = response.suggestedSheetName ?? previousSuggestedSheetName;
      importDraft.value = {
        ...importDraft.value,
        preview: response,
        availableSheetNames: response.availableSheetNames ?? [],
        suggestedSheetName: effectiveSuggestedSheetName || '',
        selectedSheetName: response.selectedSheetName
          ?? importDraft.value.selectedSheetName
          ?? effectiveSuggestedSheetName
          ?? ''
      };
      return true;
    } catch (error: unknown) {
      const data = (error as { response?: { data?: { title?: string; detail?: string } } })?.response?.data;
      if (data?.title) {
        notificationsStore.addProblem(data as { title: string; detail?: string; errors?: Record<string, string[]> });
      } else {
        notificationsStore.addError('Kunne ikke lese fil');
      }
      return false;
    } finally {
      isPreviewingImport.value = false;
    }
  }

  /**
   * Utfører importen av menypunktene i gjeldende draft-preview. Eier feilhåndtering
   * (via ApplyMenuStructureImport) og refresher menytreet ved suksess. Returnerer
   * true ved suksess, ellers false. No-op (false) når det ikke er noe å opprette.
   */
  async function applyMenuImport (): Promise<boolean> {
    const toCreate = importDraft.value.preview?.toCreate;
    if (!toCreate?.length) return false;
    isApplyingImport.value = true;
    try {
      return await ApplyMenuStructureImport({
        rootMenuItemKey: importDraft.value.rootMenuItemKey,
        items: toCreate
      });
    } finally {
      isApplyingImport.value = false;
    }
  }

  async function ApplyMenuStructureImport (request: MenuStructureApplyRequest): Promise<boolean> {
    try {
      const result = await applyMenuStructureImport(request);
      await refreshMenusAndTree();
      notificationsStore.addSuccess(
        result.createdCount > 0
          ? `Opprettet ${result.createdCount} menypunkt(er).`
          : 'Ingen nye menypunkter (alt fantes fra før).'
      );
      return true;
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke importere menystruktur');
      }
      return false;
    }
  }

  /**
   * Kjører menyvalideringen mot endepunktet og eier feil-mappingen (ProblemDetails →
   * addProblem, ellers addError). Returnerer resultatet ved suksess, eller null når
   * valideringen feilet (etter å ha varslet brukeren). FE er presentasjonslag —
   * rått endepunkt-kall + feil-routing hører hjemme her, ikke i komponenten.
   */
  async function validateMenu (): Promise<MenuIntegrityValidationResponse | null> {
    try {
      return await validateMenuIntegrity();
    } catch (error: unknown) {
      const problem = (error as { response?: { data?: { title?: string } } }).response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Validering feilet', 'Kunne ikke validere meny og avhengigheter.');
      }
      return null;
    }
  }

  // --- Shortcut target-handlinger (eier try/catch + feil-mapping for MenuTab) ----

  /**
   * Henter konfigurerte koblingsmål for et menypunkt. Returnerer listen, eller
   * null ved feil (etter å ha varslet brukeren). MenuTab eier loading-flagget.
   */
  async function loadShortcutTargets (
    menuItemKey: string
  ): Promise<MenuItem['shortcutTargets'] | null> {
    try {
      const targets = await getSystemObjectTargets(menuItemKey);
      return Array.isArray(targets) ? [...targets] : [];
    } catch (error: unknown) {
      console.error('[storeMenu] Feil ved henting av koblinger:', error);
      notificationsStore.addError('Kunne ikke hente koblinger', 'Prøv å laste siden på nytt.');
      return null;
    }
  }

  /** Kobler et mål til et menypunkt. Returnerer true ved suksess. */
  async function connectShortcutTarget (
    menuItemKey: string,
    request: SystemObjectTargetPayload
  ): Promise<boolean> {
    try {
      await addSystemObjectTarget(menuItemKey, request);
      return true;
    } catch (error: unknown) {
      console.error('[storeMenu] Feil ved kobling av target:', error);
      notificationsStore.addError('Kunne ikke koble target', getRequestErrorDetail(error));
      return false;
    }
  }

  /** Fjerner en kobling fra et menypunkt. Returnerer true ved suksess. */
  async function disconnectShortcutTarget (
    menuItemKey: string,
    request: SystemObjectTargetPayload
  ): Promise<boolean> {
    try {
      await removeSystemObjectTarget(menuItemKey, request);
      return true;
    } catch (error: unknown) {
      console.error('[storeMenu] Feil ved frakobling av target:', error);
      notificationsStore.addError('Kunne ikke koble fra target', getRequestErrorDetail(error));
      return false;
    }
  }

  /** Setter en kobling som standard for menypunktet. Returnerer true ved suksess. */
  async function setDefaultShortcutTarget (
    menuItemKey: string,
    request: SystemObjectTargetPayload
  ): Promise<boolean> {
    try {
      await setDefaultSystemObjectTarget(menuItemKey, request);
      return true;
    } catch (error: unknown) {
      console.error('[storeMenu] Feil ved valg av standard-target:', error);
      notificationsStore.addError('Kunne ikke sette standard-target', getRequestErrorDetail(error));
      return false;
    }
  }

  /**
   * Persisterer en typeendring (mappe/kobling) på et menypunkt. Egen handling fordi
   * MenuItemSave eier sin egen "Kunne ikke lagre menyelement"-varsling; typeendring
   * trenger sin egen ledetekst + feildetalj.
   */
  async function saveMenuItemTypeChange (
    payload: SystemObjectSavePayload
  ): Promise<boolean> {
    try {
      await saveSystemObject(payload);
      await refreshMenusAndTree();
      return true;
    } catch (error: unknown) {
      console.error('[storeMenu] Feil ved lagring av type endring:', error);
      notificationsStore.addError('Kunne ikke lagre typeendring', getRequestErrorDetail(error));
      return false;
    }
  }

  /**
   * Varsler om en mislykket koblingssøk-forespørsel. MenuTab eier abort-guarden og
   * requestId-logikken; storen eier kun feil-mappingen + varslingen.
   */
  function notifyTargetSearchFailed (error: unknown): void {
    notificationsStore.addError('Kunne ikke søke etter koblinger', getRequestErrorDetail(error));
  }

  return {
    // State
    menuItems,
    mapOfSystemObjects,
    isMenuLoading,
    isMenuLoaded,
    menuError,
    activeParentIds,
    breadcrumbs,
    menuTypes,
    isProcessing,
    importDraft,
    isPreviewingImport,
    isApplyingImport,
    // Computed
    getMenuTypeName,
    // Actions
    refreshMenusAndTree,
    getMenu,
    getSystemObject,
    getRoute,
    getIcon,
    getMenuItemIcon,
    getEffectiveObjectType,
    isRouteValid,
    getBreadcrumbForItem,
    updateBreadcrumbs,
    MenuItemSave,
    searchMenuSetupTargets,
    createShortcutWithPrimaryTarget,
    MenuItemDelete,
    MenuItemUnDelete,
    MenuSaveNewOrder,
    ExportMenuStructure,
    ApplyMenuStructureImport,
    startMenuImport,
    setMenuImportFile,
    setMenuImportSheetName,
    previewMenuImport,
    applyMenuImport,
    validateMenu,
    loadShortcutTargets,
    connectShortcutTarget,
    disconnectShortcutTarget,
    setDefaultShortcutTarget,
    saveMenuItemTypeChange,
    notifyTargetSearchFailed,
  };
});
